import { useEffect, useState } from 'react';

/**
 * Thème de l'interface (réglé depuis Mon compte, mémorisé sur le poste) : « Système » suit le
 * réglage clair / sombre de l'appareil, en direct ; « Clair » et « Sombre » l'imposent.
 * Les couleurs sont dans `design/tokens.css` (`data-theme` sur la racine du document).
 */
export type ThemeChoice = 'auto' | 'light' | 'dark';
export type ThemeEffectif = 'light' | 'dark';

export const THEME_KEY = 'oasis.theme';
const SOMBRE = '(prefers-color-scheme: dark)';

/** Valeur mémorisée lue prudemment : toute valeur inconnue vaut « Système ». */
export function lireChoix(valeur: string | null): ThemeChoice {
  return valeur === 'light' || valeur === 'dark' ? valeur : 'auto';
}

/** Thème réellement affiché pour un choix et la préférence de l'appareil. */
export function themeEffectif(choix: ThemeChoice, systemeSombre: boolean): ThemeEffectif {
  if (choix === 'auto') return systemeSombre ? 'dark' : 'light';
  return choix;
}

function lireStockage(): ThemeChoice {
  try {
    return lireChoix(window.localStorage.getItem(THEME_KEY));
  } catch {
    return 'auto';
  }
}

function ecrireStockage(choix: ThemeChoice): void {
  try {
    if (choix === 'auto') window.localStorage.removeItem(THEME_KEY);
    else window.localStorage.setItem(THEME_KEY, choix);
  } catch {
    // stockage indisponible (navigation privée) : préférence non conservée
  }
}

function systemeSombre(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(SOMBRE).matches
    : false;
}

/** Pose le choix sur la racine du document (« Système » : aucun attribut, la CSS suit l'appareil). */
export function appliquerTheme(choix: ThemeChoice): void {
  if (choix === 'auto') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', choix);
}

/** Au démarrage de l'application : applique le thème mémorisé, sur toutes les pages. */
export function appliquerThemeMemorise(): void {
  appliquerTheme(lireStockage());
}

/**
 * Choix du thème et thème effectif ; suit en direct un changement de préférence de l'appareil
 * (`matchMedia` « change ») pour afficher ce que donne « Système ».
 */
export function useTheme(): {
  choix: ThemeChoice;
  effectif: ThemeEffectif;
  choisir: (choix: ThemeChoice) => void;
} {
  const [choix, setChoix] = useState<ThemeChoice>(lireStockage);
  const [sombre, setSombre] = useState(systemeSombre);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(SOMBRE);
    const on = (e: MediaQueryListEvent): void => {
      setSombre(e.matches);
    };
    query.addEventListener('change', on);
    return () => {
      query.removeEventListener('change', on);
    };
  }, []);
  useEffect(() => {
    appliquerTheme(choix);
  }, [choix]);
  const choisir = (suivant: ThemeChoice): void => {
    ecrireStockage(suivant);
    setChoix(suivant);
  };
  return { choix, effectif: themeEffectif(choix, sombre), choisir };
}
